/**
 * Phase 114.D.1 — Unit Summative Task fork (Model A + common scaffolding).
 *
 * Rendered at the bottom of a unit document (between the lesson
 * sequence and the suffix). Pro-gated. Surfaces only when the unit
 * has subject + grade + strand + Big Idea + ≥2 lessons.
 *
 * Two model choices:
 *  - Standalone culminating task (Model A — shipping in 114.D.1)
 *  - Distributed unit task (Model B — placeholder, ships in 114.D.2)
 *
 * Hard rules from the spec:
 *  - Switching models clears the prior setup (with explicit
 *    confirmation in the UI)
 *  - Model A attaches ONE Gradebook assessment per unit (summative +
 *    level scale by default)
 *  - The platform NEVER calculates or implies a final mark — the
 *    Gradebook records summative evidence only
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileQuestion,
  FileText,
  Layers,
  Loader2,
  RotateCw,
  Sparkles,
  Target,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import {
  buildUnitStandaloneTaskInPlace,
  deleteUnitSummative,
  getUnitSummative,
  resumeUnitStandaloneTask,
  startUnitSummative,
  switchUnitSummativeModel,
} from "../lib/api";
import { getPendingUnitTask } from "../lib/inProgress";
import StagedBuildProgress from "./StagedBuildProgress";

// Eligibility — keep the section quiet until the unit has the
// minimum context required to populate the Summative Task Builder.
const _hasMinUnitContext = (ctx) =>
  !!(
    ctx &&
    ctx.subject &&
    ctx.grade &&
    ctx.strand &&
    (ctx.big_idea || "").trim() &&
    (ctx.lesson_count || 0) >= 2
  );

const _isPro = (user) =>
  // Use the canonical server-computed paid signal (lib/tier.js). The old check
  // read FLAT user.subscription_tier / .subscription_status, which /auth/me
  // does NOT expose (they live nested under user.subscription) — so the whole
  // summative section only rendered for admins (via has_math_engine_access)
  // and stuck non-admin pro users on a permanent "Loading…".
  !!(user?.subscription?.is_active || user?.is_admin || user?.has_math_engine_access);

const _MODEL_LABELS = {
  standalone: "Culminating Task",
  distributed: "Ongoing Task",
  test: "Test (end-of-unit)",
};

const UnitSummativeSection = ({ unitActivityId, unitContext, unitTitle, requiresSummative }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [pendingSwitchTo, setPendingSwitchTo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Retire-the-bridge: in-place Standalone task build state.
  const [building, setBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState("");
  const [buildStartedAt, setBuildStartedAt] = useState(null);
  const [blockedReason, setBlockedReason] = useState("");

  const eligible = useMemo(() => _hasMinUnitContext(unitContext), [unitContext]);
  const proUser = _isPro(user);

  const reload = useCallback(async () => {
    if (!unitActivityId || !proUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await getUnitSummative(unitActivityId);
      setState(r?.state || null);
    } catch (e) {
      // 402 etc. — treat as no-state so the section stays quiet.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [unitActivityId, proUser]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openTestBuilder = useCallback(
    (testId) => {
      // Bridge for the attach-test endpoint, mirroring the Model A
      // attach-task bridge above. The TestBuilder reads this on
      // successful generation and calls /unit-summative/{unit}/attach-test.
      try {
        sessionStorage.setItem(
          "atomicedu_pending_unit_summative_attach_test",
          JSON.stringify({
            unit_activity_id: unitActivityId,
            return_to: window.location.pathname + window.location.search,
          }),
        );
      } catch (_) {/* sessionStorage blocked — graceful no-op */}
      if (testId) {
        navigate(`/tests/${testId}`);
      } else {
        navigate(`/tests/new?unit_activity_id=${encodeURIComponent(unitActivityId)}`);
      }
    },
    [unitActivityId, navigate],
  );

  const finishTaskBuild = useCallback(async (job) => {
    if (job?.blocked) {
      setBlockedReason(
        job.block_reason ||
          "We couldn't design a rich culminating task from these expectations.",
      );
      setBuilding(false);
      return;
    }
    setBuilding(false);
    setBuildProgress("");
    setBlockedReason("");
    await reload();
    toast.success("Culminating task ready", {
      description: "Attached to this unit — your Gradebook has a new summative column.",
    });
  }, [reload]);

  const buildStandaloneInPlace = useCallback(async () => {
    setBlockedReason("");
    setBuilding(true);
    setBuildProgress("");
    setBuildStartedAt(Date.now());
    try {
      const job = await buildUnitStandaloneTaskInPlace(
        unitActivityId,
        {
          grade: unitContext.grade || "",
          subject: unitContext.subject || "",
          strand: unitContext.strand || "",
          expectations: unitContext.curriculum_codes || [],
          big_idea: unitContext.big_idea || "",
          transferable_skills: unitContext.transferable_skills || [],
          num_lessons: unitContext.num_lessons,
          prompt: unitContext.prompt || "",
        },
        { onProgress: (msg) => setBuildProgress(msg) },
      );
      await finishTaskBuild(job);
    } catch (e) {
      setBuilding(false);
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't build the task.");
    }
  }, [unitActivityId, unitContext, finishTaskBuild]);

  // Resume an in-place task build if the teacher reloaded mid-build.
  useEffect(() => {
    if (!unitActivityId || !proUser) return undefined;
    const pending = getPendingUnitTask(unitActivityId);
    if (!pending?.jobId) return undefined;
    let cancelled = false;
    (async () => {
      setBuilding(true);
      setBuildStartedAt(pending.startedAt || Date.now());
      try {
        const job = await resumeUnitStandaloneTask(unitActivityId, pending.jobId, {
          onProgress: (msg) => {
            if (!cancelled) setBuildProgress(msg);
          },
        });
        if (!cancelled) await finishTaskBuild(job);
      } catch (e) {
        if (!cancelled) setBuilding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitActivityId, proUser]);

  // Hide entirely for non-Pro OR unit-too-thin scenarios. We do not
  // surface a paywall here — the teacher gets that via the broader
  // tier-gate when they try to act on linked features (Gradebook).
  //
  // Phase 118.A.2.b — exception: when the unit is flagged as
  // `requires_summative_model`, we ALWAYS render so the mandatory
  // modal can fire. Eligible / proUser guards aren't applicable
  // because the unit is mid-finalisation; the platform should still
  // demand the choice.
  if (!requiresSummative && (!proUser || !eligible)) return null;

  const handlePick = async (model) => {
    setBusy(true);
    try {
      await startUnitSummative(unitActivityId, {
        model,
        subject: unitContext.subject,
        grade: unitContext.grade,
        strand: unitContext.strand,
        big_idea: unitContext.big_idea,
        reporting_period: unitContext.reporting_period || "",
      });
      await reload();
      // Phase 118.A.2 — for the Test model, jump the teacher straight
      // into the Test Builder so they don't have to find the entry
      // point themselves. Set the attach bridge first.
      if (model === "test") {
        openTestBuilder(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't pick that model.");
    } finally {
      setBusy(false);
    }
  };

  const handleSwitchConfirm = async () => {
    if (!pendingSwitchTo) return;
    setBusy(true);
    try {
      await switchUnitSummativeModel(unitActivityId, {
        model: pendingSwitchTo,
        subject: unitContext.subject,
        grade: unitContext.grade,
        strand: unitContext.strand,
        big_idea: unitContext.big_idea,
        reporting_period: unitContext.reporting_period || "",
      });
      setPendingSwitchTo(null);
      await reload();
      toast.success("Switched model — fresh start.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch models.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        "Remove the summative attached to this unit?\n\nThis will also remove the Gradebook column for this task. Marks already recorded will be lost. This cannot be undone.",
      )
    )
      return;
    setBusy(true);
    try {
      await deleteUnitSummative(unitActivityId);
      await reload();
      toast.success("Summative removed.");
    } catch (e) {
      toast.error("Couldn't remove the summative.");
    } finally {
      setBusy(false);
    }
  };

  // ---- Render ----
  // Phase 118.A.2.b — Mandatory at unit creation. If the unit was
  // generated post-118.A.2.b (`inputs.requires_summative_model`) and
  // the teacher hasn't picked a model yet, render a blocking modal
  // overlay containing the same fork. The inline section below stays
  // hidden until the choice is made so the entire unit page feels
  // un-finalised. Older units lack the flag → no enforcement, no
  // retroactive disruption.
  const showMandatoryModal = !!requiresSummative && !loading && !state;

  return (
    <section
      className="mt-12 pt-10 border-t-2 border-dashed border-[#96ded1]/60"
      data-testid="unit-summative-section"
    >
      <header className="flex items-start gap-3 mb-5">
        <span className="h-9 w-9 shrink-0 rounded-md bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d]">
          <Target className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight">
            Summative Assessment
          </h2>
          <p className="mt-1 text-xs text-[#4a6984]/80 font-medium max-w-xl">
            How will students demonstrate their learning at the end of this unit?
            The platform records evidence — your final report card level stays your
            professional judgment.
          </p>
        </div>
      </header>

      {showMandatoryModal ? (
        <div
          className="fixed inset-0 z-50 bg-[#1c2d3d]/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
          data-testid="unit-summative-mandatory-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mandatory-summative-title"
        >
          <div className="bg-white rounded-2xl border border-[#4a6984]/15 shadow-2xl w-full max-w-4xl p-5 sm:p-6 my-3 sm:my-6 overflow-x-hidden">
            <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/70">
              Final step · Required
            </p>
            <h3
              id="mandatory-summative-title"
              className="mt-1 font-black text-xl sm:text-2xl text-[#1c2d3d] tracking-tight break-words"
            >
              Choose how you&apos;ll assess this unit
            </h3>
            <p className="mt-2 text-sm text-[#4a6984] max-w-2xl">
              Every Atomic Edu unit ends with a summative — that&apos;s how the
              Gradebook entry materialises. Pick one of the three models to
              finalise <strong>{unitTitle || "this unit"}</strong>.
            </p>
            <div className="mt-5">
              <ModelFork onPick={handlePick} busy={busy} />
            </div>
            <p className="mt-4 text-[11px] text-[#4a6984]/60 italic">
              You can switch models later — picking now just sets the unit up so
              the Gradebook, Coverage Tracker, and Planner all know what to
              expect.
            </p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#4a6984]/70">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : showMandatoryModal ? (
        // The inline section stays a quiet placeholder while the modal
        // is up — the modal is the focused decision point.
        <div className="rounded-xl border border-dashed border-[#4a6984]/25 bg-[#f4f5f7] p-6 text-center">
          <p className="text-sm text-[#4a6984]/70">
            Pick a summative model above to finalise this unit.
          </p>
        </div>
      ) : !state ? (
        <ModelFork onPick={handlePick} busy={busy} />
      ) : state.model === "standalone" ? (
        building ? (
          <StagedBuildProgress
            progress={buildProgress}
            estimateSeconds={45}
            startedAt={buildStartedAt}
            model="task_only"
          />
        ) : (
          <>
            {blockedReason ? (
              <div
                className="mb-4 rounded-xl border-2 border-[#b45309]/30 bg-[#fffbeb] p-4"
                data-testid="unit-task-blocked"
              >
                <p className="text-sm font-black text-[#1c2d3d]">
                  A culminating task didn&apos;t fit these expectations
                </p>
                <p className="mt-1 text-sm text-[#4a6984]" data-testid="unit-task-blocked-reason">
                  {blockedReason}
                </p>
                <p className="mt-2 text-sm text-[#4a6984]/80">
                  Try the Test model instead, or switch the unit&apos;s expectations.
                </p>
              </div>
            ) : null}
            <StandaloneCard
              state={state}
              unitTitle={unitTitle}
              onOpenBuilder={buildStandaloneInPlace}
              onSwitch={() => setPendingSwitchTo("distributed")}
              onRemove={handleRemove}
              busy={busy}
            />
          </>
        )
      ) : state.model === "test" ? (
        <TestCard
          state={state}
          unitTitle={unitTitle}
          onOpenTest={() => openTestBuilder(state.test_id)}
          onSwitch={() => setPendingSwitchTo("standalone")}
          onRemove={handleRemove}
          busy={busy}
        />
      ) : (
        <DistributedSurface
          state={state}
          unitTitle={unitTitle}
          unitContext={unitContext}
          unitActivityId={unitActivityId}
          onSaved={(next) => {
            if (next) setState(next);
            else reload();
          }}
          onSwitch={() => setPendingSwitchTo("standalone")}
          onRemove={handleRemove}
          busy={busy}
        />
      )}

      {/* Switch-model confirmation dialog */}
      <Dialog
        open={!!pendingSwitchTo}
        onOpenChange={(o) => {
          if (!o) setPendingSwitchTo(null);
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="unit-summative-switch-confirm"
        >
          <DialogHeader>
            <DialogTitle>Switch to {_MODEL_LABELS[pendingSwitchTo]}?</DialogTitle>
            <DialogDescription className="pt-1">
              This will clear the current summative setup for this unit, including
              any attached task and its Gradebook column. Any marks already
              recorded on that column will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setPendingSwitchTo(null)}
              className="px-3 py-2 rounded-md border border-[#4a6984]/30 text-[#4a6984] text-xs font-bold hover:bg-[#f4f5f7]"
              data-testid="unit-summative-switch-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSwitchConfirm}
              disabled={busy}
              className="px-3 py-2 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 disabled:bg-[#4a6984]/40 text-white text-xs font-extrabold inline-flex items-center gap-1.5"
              data-testid="unit-summative-switch-confirm-btn"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCw className="h-3.5 w-3.5" />
              )}
              Switch
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ---- Sub-components -----------------------------------------------------

const ModelFork = ({ onPick, busy }) => (
  <div className="grid md:grid-cols-3 gap-3" data-testid="unit-summative-fork">
    <ModelCard
      icon={<Target className="h-4 w-4" />}
      title="Culminating Task"
      blurb="Students complete one focused task at the end of the unit that shows everything they've learned."
      ctaLabel="Choose Culminating"
      onClick={() => onPick("standalone")}
      busy={busy}
      testid="unit-summative-pick-standalone"
    />
    <ModelCard
      icon={<Layers className="h-4 w-4" />}
      title="Ongoing Task"
      blurb="Students build one piece at a time across the unit until it all comes together."
      ctaLabel="Choose Ongoing"
      onClick={() => onPick("distributed")}
      busy={busy}
      testid="unit-summative-pick-distributed"
    />
    <ModelCard
      icon={<FileQuestion className="h-4 w-4" />}
      title="Test (end-of-unit)"
      blurb="A mixed-format test (multiple choice, true/false, matching, short and long answer) tagged to the four Ontario achievement categories."
      ctaLabel="Choose Test"
      onClick={() => onPick("test")}
      busy={busy}
      testid="unit-summative-pick-test"
      badge="New · 118.A"
    />
  </div>
);

const ModelCard = ({ icon, title, blurb, ctaLabel, onClick, busy, testid, badge }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    data-testid={testid}
    className="text-left rounded-xl border border-[#4a6984]/15 hover:border-[#1c2d3d] hover:shadow-sm transition-all bg-white p-5 disabled:opacity-60"
  >
    <div className="flex items-start gap-3">
      <span className="h-8 w-8 rounded-md bg-[#96ded1]/30 text-[#1c2d3d] grid place-items-center shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight">
          {title}
        </p>
        <p className="text-[12px] text-[#4a6984] leading-snug mt-1">{blurb}</p>
        {badge ? (
          <p className="mt-2 inline-block text-[9.5px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/70 bg-[#f4f5f7] border border-[#4a6984]/15 rounded px-1.5 py-0.5">
            {badge}
          </p>
        ) : null}
      </div>
    </div>
    <p className="mt-3 inline-flex items-center gap-1 text-[11px] font-extrabold text-[#1c2d3d]">
      {ctaLabel}
      <ArrowRight className="h-3 w-3" />
    </p>
  </button>
);

// Phase 118.A.2 — Test summative card. Mirrors StandaloneCard but
// links to the Test Builder. When `state.test_id` is null the test
// hasn't been generated yet (teacher picked the model but bailed
// before generating); we show a "Resume" CTA that re-opens the
// builder.
const TestCard = ({ state, unitTitle, onOpenTest, onSwitch, onRemove, busy }) => {
  const attached = !!state.test_id && state.status === "task_attached";
  return (
    <div
      className="rounded-xl border border-[#96ded1]/70 bg-[#96ded1]/10 p-5"
      data-testid="unit-summative-test-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-extrabold text-xs uppercase tracking-[0.18em] text-[#4a6984]/75">
            Test (end-of-unit)
          </p>
          <p className="font-black text-base text-[#1c2d3d] tracking-tight mt-0.5">
            {state.task_title || unitTitle || "Unit Test"}
          </p>
          {attached ? (
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#1c2d3d]">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              Test attached · Gradebook entry created
            </p>
          ) : (
            <p className="mt-1.5 text-[12px] text-[#4a6984]">
              Open the Test Builder to generate questions tagged to the four
              Ontario achievement chart categories.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpenTest}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#1c2d3d] text-white text-xs font-bold hover:bg-[#0e1a25] disabled:opacity-40"
            data-testid="unit-summative-test-open"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {attached ? "Open Test" : "Open Builder"}
          </button>
          <button
            type="button"
            onClick={onSwitch}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#4a6984]/25 bg-white text-xs font-bold text-[#1c2d3d] hover:bg-[#f4f5f7] disabled:opacity-50"
            data-testid="unit-summative-test-switch"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Switch model
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label="Remove summative"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md text-[#4a6984] hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
            data-testid="unit-summative-test-remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};


const StandaloneCard = ({ state, unitTitle, onOpenBuilder, onSwitch, onRemove, busy }) => {
  const attached = state.status === "task_attached";
  return (
    <div
      className="rounded-xl border border-[#96ded1]/70 bg-[#96ded1]/10 p-5"
      data-testid="unit-summative-standalone-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-md bg-white text-[#1c2d3d] grid place-items-center border border-[#96ded1]/70 shrink-0">
            <Target className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80">
              Culminating Task
            </p>
            <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight mt-0.5">
              {attached
                ? state.task_title || "Summative task attached"
                : "Ready to generate"}
            </p>
            {attached ? (
              <p className="text-[11px] text-[#4a6984] mt-1 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#1c9e7e]" />
                Attached to <span className="font-bold">{unitTitle || "this unit"}</span>
                <span className="text-[#4a6984]/50">·</span>
                <span>1 Gradebook column created</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {!attached ? (
            <button
              type="button"
              onClick={onOpenBuilder}
              disabled={busy}
              data-testid="unit-summative-open-builder"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white px-3 py-2 text-[11px] font-extrabold disabled:opacity-60"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Generate culminating task
            </button>
          ) : (
            <>
              {state.task_activity_id && (
                <button
                  type="button"
                  onClick={() =>
                    window.open(`/lesson/${encodeURIComponent(state.task_activity_id)}`, "_self")
                  }
                  data-testid="unit-summative-view-task"
                  className="inline-flex items-center gap-1.5 rounded-md bg-white border border-[#1c2d3d]/20 hover:border-[#1c2d3d]/40 text-[#1c2d3d] px-3 py-2 text-[11px] font-extrabold"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open Task
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  window.open(
                    `/gradebook?unit=${encodeURIComponent(state.unit_activity_id)}`,
                    "_self",
                  )
                }
                data-testid="unit-summative-open-gradebook"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white px-3 py-2 text-[11px] font-extrabold"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
                Open in Gradebook
                <ExternalLink className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#96ded1]/40">
        <button
          type="button"
          onClick={onSwitch}
          disabled={busy}
          data-testid="unit-summative-switch-to-distributed"
          className="text-[10.5px] font-bold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-2 hover:underline disabled:opacity-60"
        >
          Switch to Ongoing
        </button>
        <span className="text-[#4a6984]/30 text-xs">·</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          data-testid="unit-summative-remove"
          className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#4a6984] hover:text-red-600 disabled:opacity-60"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
        <span className="ml-auto text-[10px] italic text-[#4a6984]/60">
          Final report card level remains your professional judgment.
        </span>
      </div>
    </div>
  );
};

// Phase 114.D.2 — Distributed unit task surface (Model B). Three states:
//   1. draft + no components yet → planner form
//   2. components_set → summary card with edit + Gradebook + PDF
//   3. Switch / Remove affordances mirror the Standalone path so the
//      teacher's mental model is consistent across both forks.
const DistributedSurface = ({
  state,
  unitTitle,
  unitContext,
  unitActivityId,
  onSaved,
  onSwitch,
  onRemove,
  busy: parentBusy,
}) => {
  const isAttached = state.status === "components_set";
  return isAttached ? (
    <DistributedAttachedCard
      state={state}
      unitTitle={unitTitle}
      unitActivityId={unitActivityId}
      onSaved={onSaved}
      onSwitch={onSwitch}
      onRemove={onRemove}
      busy={parentBusy}
    />
  ) : (
    <DistributedPlannerForm
      state={state}
      unitContext={unitContext}
      unitActivityId={unitActivityId}
      onSaved={onSaved}
      onSwitch={onSwitch}
      onRemove={onRemove}
      parentBusy={parentBusy}
    />
  );
};

const DistributedPlannerForm = ({
  state,
  unitContext,
  unitActivityId,
  onSaved,
  onSwitch,
  onRemove,
  parentBusy,
}) => {
  const existing = state?.components || [];
  const [overallTitle, setOverallTitle] = useState(state?.overall_title || "");
  const [overallCriteria, setOverallCriteria] = useState(
    state?.overall_success_criteria || "",
  );
  // Seed one row per lesson on first mount — teachers can leave rows
  // blank and they'll be filtered out on save. We never demand a
  // component for every lesson because not every lesson contributes.
  const [rows, setRows] = useState(() => {
    if (existing.length > 0) return existing.map((c) => ({ ...c }));
    const lessons = unitContext?.lessons || [];
    return lessons.map((l, idx) => ({
      lesson_index: idx + 1,
      lesson_title: l.lesson_title || `Lesson ${idx + 1}`,
      component_name: "",
      success_criterion: "",
      lesson_activity_id: l.activity_id || "",
    }));
  });
  const [saving, setSaving] = useState(false);

  const updateRow = (idx, field, value) => {
    setRows((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    const title = overallTitle.trim();
    if (!title) {
      toast.error("Give the ongoing task an overall title.");
      return;
    }
    const filled = rows.filter((r) => (r.component_name || "").trim());
    if (filled.length === 0) {
      toast.error("Add at least one component before saving.");
      return;
    }
    setSaving(true);
    try {
      const { saveDistributedComponents } = await import("../lib/api");
      const res = await saveDistributedComponents(unitActivityId, {
        overall_title: title,
        overall_success_criteria: overallCriteria.trim(),
        components: filled,
        curriculum_expectation_codes: unitContext?.curriculum_codes || [],
      });
      onSaved?.(res?.state || null);
      toast.success(
        `${filled.length} component${filled.length === 1 ? "" : "s"} saved to your Gradebook.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the ongoing task.");
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || parentBusy;

  return (
    <div
      className="rounded-xl border border-[#96ded1]/70 bg-[#96ded1]/10 p-5 space-y-4"
      data-testid="unit-summative-distributed-planner"
    >
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-md bg-white text-[#1c2d3d] grid place-items-center border border-[#96ded1]/70 shrink-0">
          <Layers className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80">
            Ongoing Task — planner
          </p>
          <p className="text-[11.5px] text-[#4a6984] leading-snug mt-0.5 max-w-xl">
            Each lesson contributes one component. The platform stores them as
            independent Gradebook columns — never as a single overall mark.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80">
            Overall task title
          </span>
          <input
            type="text"
            value={overallTitle}
            onChange={(e) => setOverallTitle(e.target.value)}
            placeholder="e.g. Fractions Mastery Project"
            data-testid="distributed-planner-title"
            className="mt-1 w-full bg-white border border-[#4a6984]/25 focus:border-[#1c2d3d] focus:outline-none rounded-md px-3 py-2 text-sm font-bold text-[#1c2d3d]"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80">
            Overall success criteria
          </span>
          <textarea
            rows={2}
            value={overallCriteria}
            onChange={(e) => setOverallCriteria(e.target.value)}
            placeholder="What does success look like across the whole task?"
            data-testid="distributed-planner-overall-criteria"
            className="mt-1 w-full bg-white border border-[#4a6984]/25 focus:border-[#1c2d3d] focus:outline-none rounded-md px-3 py-2 text-[12.5px] text-[#1c2d3d] resize-y"
          />
        </label>
      </div>

      <div>
        <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80 mb-1.5">
          Per-lesson components ({rows.filter((r) => (r.component_name || "").trim()).length} of {rows.length} filled)
        </p>
        {rows.length === 0 ? (
          <p className="text-[12px] italic text-[#4a6984]">
            No lessons yet on this unit. Add lessons first.
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r, idx) => (
              <li
                key={idx}
                data-testid={`distributed-planner-row-${idx}`}
                className="grid grid-cols-1 md:grid-cols-[110px,1fr,1fr] gap-2 items-start"
              >
                <p className="text-[11px] font-bold text-[#1c2d3d] pt-2 leading-snug">
                  Lesson {r.lesson_index}
                  <br />
                  <span className="text-[10px] font-medium text-[#4a6984]/75">
                    {r.lesson_title || "Untitled"}
                  </span>
                </p>
                <input
                  type="text"
                  value={r.component_name}
                  onChange={(e) => updateRow(idx, "component_name", e.target.value)}
                  placeholder="Component name"
                  data-testid={`distributed-planner-row-${idx}-name`}
                  className="bg-white border border-[#4a6984]/20 focus:border-[#1c2d3d] focus:outline-none rounded-md px-2.5 py-1.5 text-[12px] text-[#1c2d3d]"
                />
                <input
                  type="text"
                  value={r.success_criterion}
                  onChange={(e) =>
                    updateRow(idx, "success_criterion", e.target.value)
                  }
                  placeholder="Success criterion (optional)"
                  data-testid={`distributed-planner-row-${idx}-criterion`}
                  className="bg-white border border-[#4a6984]/20 focus:border-[#1c2d3d] focus:outline-none rounded-md px-2.5 py-1.5 text-[12px] text-[#1c2d3d]"
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-3 pt-3 border-t border-[#96ded1]/40 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          data-testid="distributed-planner-save"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 disabled:bg-[#4a6984]/40 text-white px-3 py-2 text-[11px] font-extrabold"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5" />
          )}
          Save ongoing task
        </button>
        <button
          type="button"
          onClick={onSwitch}
          disabled={busy}
          data-testid="unit-summative-switch-to-standalone"
          className="text-[10.5px] font-bold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-2 hover:underline disabled:opacity-60"
        >
          Switch to Culminating
        </button>
        <span className="text-[#4a6984]/30 text-xs">·</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          data-testid="unit-summative-remove"
          className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#4a6984] hover:text-red-600 disabled:opacity-60"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
        <span className="ml-auto text-[10px] italic text-[#4a6984]/60">
          No platform-implied final mark. Components grade independently.
        </span>
      </div>
    </div>
  );
};

const DistributedAttachedCard = ({
  state,
  unitTitle,
  unitActivityId,
  onSaved,
  onSwitch,
  onRemove,
  busy,
}) => {
  const [editing, setEditing] = useState(false);
  const components = state?.components || [];
  if (editing) {
    return (
      <DistributedPlannerForm
        state={state}
        unitContext={{
          lessons: components.map((c) => ({
            lesson_title: c.lesson_title,
            activity_id: c.lesson_activity_id || "",
          })),
        }}
        unitActivityId={unitActivityId}
        onSaved={(next) => {
          setEditing(false);
          onSaved?.(next);
        }}
        onSwitch={onSwitch}
        onRemove={onRemove}
        parentBusy={busy}
      />
    );
  }
  return (
    <div
      className="rounded-xl border border-[#96ded1]/70 bg-[#96ded1]/10 p-5"
      data-testid="unit-summative-distributed-attached"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className="h-9 w-9 rounded-md bg-white text-[#1c2d3d] grid place-items-center border border-[#96ded1]/70 shrink-0">
            <Layers className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/80">
              Ongoing Task
            </p>
            <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight mt-0.5">
              {state.overall_title || "Ongoing task"}
            </p>
            <p className="text-[11px] text-[#4a6984] mt-1 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-[#1c9e7e]" />
              {components.length} component{components.length === 1 ? "" : "s"} in {unitTitle || "this unit"}
              <span className="text-[#4a6984]/50">·</span>
              <span>{components.length} Gradebook column{components.length === 1 ? "" : "s"} created</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => {
              const base = process.env.REACT_APP_BACKEND_URL || "";
              window.open(
                `${base}/api/unit-summative/${unitActivityId}/overview-pdf`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
            data-testid="unit-summative-distributed-pdf"
            className="inline-flex items-center gap-1.5 rounded-md bg-white border border-[#1c2d3d] hover:bg-[#f4f5f7] text-[#1c2d3d] px-3 py-2 text-[11px] font-extrabold"
            title="Branded teacher-facing PDF — never goes home to parents"
          >
            <FileText className="h-3.5 w-3.5" />
            Overview PDF
          </button>
          <button
            type="button"
            onClick={() =>
              window.open(
                `/gradebook?unit=${encodeURIComponent(unitActivityId)}`,
                "_self",
              )
            }
            data-testid="unit-summative-distributed-open-gradebook"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white px-3 py-2 text-[11px] font-extrabold"
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            Open in Gradebook
          </button>
        </div>
      </div>

      <ul className="mt-4 grid sm:grid-cols-2 gap-2">
        {components.map((c, idx) => (
          <li
            key={idx}
            className="rounded-md bg-white border border-[#4a6984]/12 px-3 py-2"
            data-testid={`distributed-attached-component-${idx}`}
          >
            <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/70">
              Lesson {c.lesson_index}
            </p>
            <p className="text-[12.5px] font-bold text-[#1c2d3d] leading-snug">
              {c.component_name}
            </p>
            {c.success_criterion ? (
              <p className="text-[10.5px] text-[#4a6984] mt-0.5 leading-snug">
                {c.success_criterion}
              </p>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#96ded1]/40 flex-wrap">
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={busy}
          data-testid="distributed-attached-edit"
          className="text-[10.5px] font-bold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-2 hover:underline disabled:opacity-60"
        >
          Edit components
        </button>
        <span className="text-[#4a6984]/30 text-xs">·</span>
        <button
          type="button"
          onClick={onSwitch}
          disabled={busy}
          data-testid="unit-summative-switch-to-standalone"
          className="text-[10.5px] font-bold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-2 hover:underline disabled:opacity-60"
        >
          Switch to Culminating
        </button>
        <span className="text-[#4a6984]/30 text-xs">·</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          data-testid="unit-summative-remove"
          className="inline-flex items-center gap-1 text-[10.5px] font-bold text-[#4a6984] hover:text-red-600 disabled:opacity-60"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
        <span className="ml-auto text-[10px] italic text-[#4a6984]/60">
          No platform-implied final mark. Final level is your professional judgment.
        </span>
      </div>
    </div>
  );
};

export default UnitSummativeSection;
